import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';

import { UseCaseGroup, UseCaseTyps, UseCaseTypesEnum } from '@vi-iot/client';

import { AppState } from '../app.state';
import { RemoteDiagnosticsActiveSession } from '../remote-diagnostics/remote-diagnostics.model';
import { RemoteDiagnosticsSelectors } from '../remote-diagnostics/remote-diagnostics.selectors';
import {
  ExtendedParametersTabsEnum,
  serviceLayerGroups,
} from './remote-diagnostics-use-cases.model';

import { RemoteDiagnosticsUseCasesState } from './remote-diagnostics-use-cases.state';

export namespace RemoteDiagnosticsUseCasesSelectors {
  const remoteDiagnosticsUseCases = (state: AppState): RemoteDiagnosticsUseCasesState =>
    state.remoteDiagnosticsUseCases;

  export const selectVddListForUseCaseLoading$ = createSelector(
    remoteDiagnosticsUseCases,
    (state): boolean => state.vddListLoading
  );

  export function isLoading(): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      remoteDiagnosticsUseCases,
      (state: RemoteDiagnosticsUseCasesState) => state.loading
    );
  }

  export function getUseCases(): MemoizedSelector<
    AppState,
    UseCaseTyps[],
    DefaultProjectorFn<UseCaseTyps[]>
  > {
    return createSelector(
      remoteDiagnosticsUseCases,
      (state: RemoteDiagnosticsUseCasesState) => state.useCases?.structure
    );
  }

  export function getUseCaseGroups(
    gatewaySerial: string
  ): MemoizedSelector<AppState, UseCaseGroup[], DefaultProjectorFn<UseCaseGroup[]>> {
    return createSelector(
      remoteDiagnosticsUseCases,
      RemoteDiagnosticsUseCasesSelectors.getSelectedUseCaseType(gatewaySerial),
      (state: RemoteDiagnosticsUseCasesState, useCaseType: UseCaseTypesEnum) => {
        const selectedUseCaseType = state.useCases.structure.find(
          (useCase) => useCase.usecasetype === useCaseType
        );

        if (useCaseType === UseCaseTypesEnum.ServiceLayer) {
          return serviceLayerGroups;
        }

        return selectedUseCaseType?.structure;
      }
    );
  }

  export function getSelectedExtendedParametersTab(
    gatewaySerial: string
  ): MemoizedSelector<
    AppState,
    ExtendedParametersTabsEnum,
    DefaultProjectorFn<ExtendedParametersTabsEnum>
  > {
    return createSelector(
      RemoteDiagnosticsSelectors.getActiveSessions(),
      (activeSessions: RemoteDiagnosticsActiveSession[]) =>
        activeSessions.find((session) => session.gatewaySerial === gatewaySerial)
          ?.selectedExtendedParametersTab
    );
  }

  export function getSelectedUseCaseType(
    gatewaySerial: string
  ): MemoizedSelector<AppState, UseCaseTypesEnum, DefaultProjectorFn<UseCaseTypesEnum>> {
    return createSelector(
      RemoteDiagnosticsSelectors.getActiveSessions(),
      (activeSessions: RemoteDiagnosticsActiveSession[]) =>
        activeSessions.find((session) => session.gatewaySerial === gatewaySerial)
          ?.selectedUseCaseType
    );
  }

  export function getSelectedUseCaseGroup(
    gatewaySerial: string
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(
      RemoteDiagnosticsSelectors.getActiveSessions(),
      (activeSessions: RemoteDiagnosticsActiveSession[]) =>
        activeSessions.find((session) => session.gatewaySerial === gatewaySerial)
          ?.selectedUseCaseGroupe
    );
  }

  export function getVddListLoaded(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      remoteDiagnosticsUseCases,
      RemoteDiagnosticsUseCasesSelectors.getSelectedUseCaseType(gatewaySerial),
      (state: RemoteDiagnosticsUseCasesState, useCaseType: UseCaseTypesEnum) => {
        if (useCaseType === UseCaseTypesEnum.ServiceLayer) {
          return true;
        }

        return state.vddListLoaded;
      }
    );
  }
}
